import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LuMenu,
  LuX,
  LuChevronDown,
  LuChartLine,
  LuLayers,
  LuHouse,
  LuRadio,
  LuLayoutGrid,
  LuBookOpen,
  LuInfo,
  LuBookMarked,
  LuPhone,
  LuStar,
  LuCrown,
} from "react-icons/lu";
import { FaWhatsapp } from "react-icons/fa";
import DownloadButton from "./DownloadButton";
import { useContact, useMarkets } from "../hooks/useData";
import { APK_URL, BRAND } from "../utils/config";
import { trackDownload } from "../utils/analytics";

const MarketLinks = ({ markets, kind, onNavigate }) => (
  <div className="rt-mega__list">
    {markets.map((m) => (
      <Link key={m.slug} to={`/${kind}/${m.slug}`} className="rt-mega__link" onClick={onNavigate}>
        {m.name}
        <span>{kind === "jodi" ? "Jodi" : "Pana"}</span>
      </Link>
    ))}
  </div>
);

const DRAWER_LINKS = [
  { to: "/", label: "Home", icon: LuHouse, end: true },
  { to: "/matka-result-today", label: "Today's Result", icon: LuRadio },
  { to: "/charts", label: "All Charts", icon: LuLayoutGrid },
  { to: "/starline", label: "King Starline", icon: LuStar },
  { to: "/jackpot", label: "King Jackpot", icon: LuCrown },
  { to: "/satta-matka", label: "Satta Matka Guide", icon: LuBookMarked },
  { to: "/how-to-play", label: "How to Play", icon: LuBookOpen },
  { to: "/about", label: "About Us", icon: LuInfo },
];

export const Header = () => {
  const { markets } = useMarkets();
  const { whatsapp, mobile } = useContact();
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [group, setGroup] = useState("jodi");
  const [charts, setCharts] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const panelRef = useRef(null);
  const burgerRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setMega(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // lock page scroll, close on Escape, move focus into the menu and back
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => panelRef.current && panelRef.current.querySelector("a,button")?.focus(), 60);
    const btn = burgerRef.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      document.body.style.overflow = "";
      btn && btn.focus();
    };
  }, [open]);

  // close the drawer if the window grows to desktop width
  useEffect(() => {
    const onResize = () => window.innerWidth >= 1100 && setOpen(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      <header className={`rt-header${scrolled ? " is-scrolled" : ""}`}>
        <div className="rt-wrap rt-header__row">
          <Link to="/" className="rt-brand" aria-label={`${BRAND.name} home`}>
            <img src="/logo.svg" alt="" width="44" height="44" />
            <span>
              <b>{BRAND.name}</b>
              <small>{BRAND.tagline}</small>
            </span>
          </Link>

          <nav className="rt-nav" aria-label="Main">
            <NavLink to="/" end>
              Home
            </NavLink>
            <NavLink to="/matka-result-today">Today&apos;s Result</NavLink>
            <NavLink to="/charts">All Charts</NavLink>
            <div
              className={`rt-nav__mega${mega ? " is-open" : ""}`}
              onMouseEnter={() => setMega(true)}
              onMouseLeave={() => setMega(false)}
            >
              <button type="button" aria-expanded={mega} onClick={() => setMega((v) => !v)}>
                Game Charts <LuChevronDown size={15} aria-hidden="true" />
              </button>
              <div className="rt-mega" role="menu">
                <div className="rt-mega__col">
                  <div className="rt-mega__title">
                    <LuChartLine size={16} aria-hidden="true" /> Jodi Charts
                  </div>
                  <MarketLinks markets={markets} kind="jodi" onNavigate={() => setMega(false)} />
                </div>
                <div className="rt-mega__col">
                  <div className="rt-mega__title">
                    <LuLayers size={16} aria-hidden="true" /> Pana Charts
                  </div>
                  <MarketLinks markets={markets} kind="pana" onNavigate={() => setMega(false)} />
                </div>
              </div>
            </div>
            <NavLink to="/starline">Starline</NavLink>
            <NavLink to="/jackpot">Jackpot</NavLink>
            <NavLink to="/satta-matka">Satta Matka</NavLink>
          </nav>

          <div className="rt-header__actions">
            <DownloadButton compact />
            <button
              ref={burgerRef}
              type="button"
              className="rt-burger"
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls="rt-drawer"
              onClick={() => setOpen(true)}
            >
              <LuMenu size={24} />
            </button>
          </div>
        </div>
      </header>

      <div id="rt-drawer" className={`rt-drawer${open ? " is-open" : ""}`} aria-hidden={!open} role="dialog" aria-modal="true" aria-label="Menu">
        <button type="button" className="rt-drawer__backdrop" aria-label="Close menu" tabIndex={open ? 0 : -1} onClick={() => setOpen(false)} />
        <aside className="rt-drawer__panel" ref={panelRef}>
          <div className="rt-drawer__head">
            <Link to="/" className="rt-brand" onClick={() => setOpen(false)}>
              <img src="/logo.svg" alt="" width="40" height="40" />
              <span>
                <b>{BRAND.name}</b>
                <small>{BRAND.tagline}</small>
              </span>
            </Link>
            <button type="button" className="rt-drawer__close" aria-label="Close menu" onClick={() => setOpen(false)}>
              <LuX size={22} />
            </button>
          </div>

          <div className="rt-drawer__body">
            <nav aria-label="Menu">
              {DRAWER_LINKS.slice(0, 3).map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} className="rt-drawer__link">
                  <l.icon size={20} aria-hidden="true" /> {l.label}
                </NavLink>
              ))}

              <div className={`rt-drawer__acc${charts ? " is-open" : ""}`}>
                <button type="button" className="rt-drawer__link" aria-expanded={charts} onClick={() => setCharts((v) => !v)}>
                  <LuChartLine size={20} aria-hidden="true" /> Game Charts
                  <LuChevronDown size={18} className="rt-drawer__chev" aria-hidden="true" />
                </button>
                {charts && (
                  <div className="rt-drawer__sub">
                    <div className="rt-tabs" role="tablist">
                      <button type="button" role="tab" aria-selected={group === "jodi"} className={group === "jodi" ? "is-on" : ""} onClick={() => setGroup("jodi")}>
                        Jodi
                      </button>
                      <button type="button" role="tab" aria-selected={group === "pana"} className={group === "pana" ? "is-on" : ""} onClick={() => setGroup("pana")}>
                        Pana
                      </button>
                    </div>
                    <div className="rt-drawer__chips">
                      {markets.map((m) => (
                        <Link key={m.slug} to={`/${group}/${m.slug}`} onClick={() => setOpen(false)}>
                          {m.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {DRAWER_LINKS.slice(3).map((l) => (
                <NavLink key={l.to} to={l.to} className="rt-drawer__link">
                  <l.icon size={20} aria-hidden="true" /> {l.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="rt-drawer__foot">
            <DownloadButton className="rt-dl--block" />
            {(whatsapp || mobile) && (
              <div className="rt-drawer__contact">
                {mobile && (
                  <a href={`tel:+91${mobile.replace(/^91/, "")}`}>
                    <LuPhone size={16} aria-hidden="true" /> Call
                  </a>
                )}
                {whatsapp && (
                  <a href={`https://wa.me/91${whatsapp.replace(/^91/, "")}`} target="_blank" rel="noopener noreferrer" className="is-wa">
                    <FaWhatsapp size={16} aria-hidden="true" /> WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </>
  );
};

export const FloatingActions = () => {
  const { whatsapp } = useContact();
  const { pathname } = useLocation();
  const [heroCtaVisible, setHeroCtaVisible] = useState(false);

  // The sticky bar only appears once the in-page Download button has scrolled out of view.
  useEffect(() => {
    const el = document.querySelector("[data-hero-cta]");
    if (!el) {
      setHeroCtaVisible(false);
      return undefined;
    }
    const check = () => {
      const r = el.getBoundingClientRect();
      setHeroCtaVisible(r.bottom > 70 && r.top < window.innerHeight);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [pathname]);

  return (
    <>
      {whatsapp && (
        <a
          className="rt-wa"
          href={`https://wa.me/91${whatsapp.replace(/^91/, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
        >
          <FaWhatsapp size={28} aria-hidden="true" />
        </a>
      )}
      <div className={`rt-sticky${heroCtaVisible ? " is-hidden" : ""}`} aria-hidden={heroCtaVisible}>
        <a href={APK_URL} download rel="nofollow noopener" onClick={trackDownload}>
          Download {BRAND.name} App
        </a>
      </div>
    </>
  );
};

export const Footer = () => {
  const { markets } = useMarkets();
  return (
    <footer className="rt-footer">
      <div className="rt-wrap">
        <div className="rt-footer__grid">
          <div>
            <Link to="/" className="rt-brand rt-brand--foot" aria-label={`${BRAND.name} home`}>
              <img src="/logo.svg" alt="" width="44" height="44" loading="lazy" />
              <span>
                <b>{BRAND.name}</b>
                <small>Official &amp; Verified</small>
              </span>
            </Link>
            <p className="rt-footer__about">
              Satta matka result today, live Jodi and Panel charts and market timings, updated as soon as every result
              is declared. Information only — 18+.
            </p>
          </div>
          <nav aria-label="Navigation">
            <h3>Navigation</h3>
            <Link to="/">Home</Link>
            <Link to="/matka-result-today">Matka Result Today</Link>
            <Link to="/charts">All Charts</Link>
            <Link to="/starline">King Starline</Link>
            <Link to="/jackpot">King Jackpot</Link>
            <Link to="/satta-matka">Satta Matka Guide</Link>
            <Link to="/how-to-play">How to Play</Link>
            <Link to="/about">About Us</Link>
          </nav>
          <nav aria-label="Legal">
            <h3>Legal</h3>
            <Link to="/terms">Terms &amp; Conditions</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/disclaimer">Disclaimer</Link>
          </nav>
          <div>
            <h3>Why trust us</h3>
            <ul className="rt-footer__trust">
              <li>Results published after declaration</li>
              <li>Week-by-week Jodi &amp; Pana charts</li>
              <li>24/7 help on WhatsApp</li>
            </ul>
          </div>
        </div>

        {markets.length > 0 && (
          <nav className="rt-footer__markets" aria-label="Matka charts">
            <h3>Satta Matka Charts</h3>
            <div>
              {markets.map((m) => (
                <span key={m.slug}>
                  <Link to={`/jodi/${m.slug}`}>{m.name} Jodi</Link>
                  <Link to={`/pana/${m.slug}`}>Pana</Link>
                </span>
              ))}
            </div>
          </nav>
        )}

        <div className="rt-footer__stats">
          <div>
            <strong>{markets.length || "20"}</strong>
            <span>Live Markets</span>
          </div>
          <div>
            <strong>24/7</strong>
            <span>Support</span>
          </div>
          <div>
            <strong>18+</strong>
            <span>Players only</span>
          </div>
          <div>
            <strong>Free</strong>
            <span>Charts &amp; results</span>
          </div>
        </div>

        <p className="rt-footer__note">
          Disclaimer: This website publishes result data for information only. Gambling is restricted or prohibited in
          many places — check the law where you live. Never wager money you cannot afford to lose.
        </p>
        <p className="rt-footer__copy">
          © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
        </p>
      </div>
    </footer>
  );
};
